import { useEffect, useRef, useState } from 'react';
import { Modal, message } from 'antd';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import { io, Socket } from 'socket.io-client';
import 'xterm/css/xterm.css';

interface TerminalModalProps {
  visible: boolean;
  instanceId: string;
  instanceName: string;
  onClose: () => void;
}

export default function TerminalModal({
  visible,
  instanceId,
  instanceName,
  onClose,
}: TerminalModalProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminal = useRef<Terminal | null>(null);
  const socket = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!visible || !terminalRef.current) return;

    // Initialize terminal
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
      },
      rows: 30,
      cols: 100,
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();

    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);
    term.open(terminalRef.current);
    fitAddon.fit();

    terminal.current = term;

    // Connect WebSocket
    const socketUrl = window.location.protocol === 'https:' 
      ? `wss://${window.location.hostname}:8000`
      : `ws://${window.location.hostname}:8000`;
    const sock = io(`${socketUrl}/terminal`, {
      auth: {
        token: localStorage.getItem('token'),
      },
    });

    socket.current = sock;

    sock.on('connect', () => {
      term.writeln('🔌 Connecting to instance...');
      
      // Get user info from localStorage
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      
      sock.emit('terminal:connect', {
        instanceId,
        organizationId: user.organizationId,
      });
    });

    sock.on('terminal:connected', () => {
      setConnected(true);
      term.clear();
      term.writeln('✅ Connected to instance!');
      term.writeln(`Instance: ${instanceName}`);
      term.writeln('─'.repeat(60));
    });

    sock.on('terminal:output', (data: string) => {
      term.write(data);
    });

    sock.on('terminal:error', (error: any) => {
      term.writeln(`\r\n❌ Error: ${error.message}`);
      message.error(`Terminal error: ${error.message}`);
    });

    sock.on('terminal:disconnected', () => {
      term.writeln('\r\n🔌 Disconnected from instance');
      setConnected(false);
    });

    sock.on('disconnect', () => {
      term.writeln('\r\n🔌 Connection lost');
      setConnected(false);
    });

    // Handle terminal input
    term.onData((data) => {
      sock.emit('terminal:input', data);
    });

    // Handle resize
    const handleResize = () => {
      fitAddon.fit();
      sock.emit('terminal:resize', {
        cols: term.cols,
        rows: term.rows,
      });
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      term.dispose();
      sock.disconnect();
    };
  }, [visible, instanceId, instanceName]);

  const handleClose = () => {
    if (socket.current) {
      socket.current.disconnect();
    }
    onClose();
  };

  return (
    <Modal
      title={`Terminal - ${instanceName}`}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={900}
      bodyStyle={{ padding: 0, background: '#1e1e1e' }}
      destroyOnClose
    >
      <div
        ref={terminalRef}
        style={{
          height: '600px',
          padding: '10px',
          background: '#1e1e1e',
        }}
      />
      {connected && (
        <div style={{
          padding: '8px 16px',
          background: '#2d2d2d',
          borderTop: '1px solid #444',
          color: '#888',
          fontSize: '12px',
        }}>
          Press Ctrl+C to interrupt, Ctrl+D to exit
        </div>
      )}
    </Modal>
  );
}
